/**
 * @sazeo/sdk — Tenant Announcements hooks
 *
 * Direct Supabase client calls (no Edge Function proxy) — same pattern as
 * calendar.ts. RLS enforces tenant isolation and publish-state visibility.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TenantAnnouncement {
  id: string;
  tenant_id: string;
  title: string;
  body: string;
  pinned: boolean;
  published_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementCreateInput {
  title: string;
  body: string;
  pinned?: boolean;
  published_at?: string | null;
  expires_at?: string | null;
}

export interface AnnouncementUpdateInput extends Partial<AnnouncementCreateInput> {}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const announcementKeys = {
  all: ['announcements'] as const,
  list: (tenantId: string) => [...announcementKeys.all, 'list', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAnnouncements(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: announcementKeys.list(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tenant_announcements')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('pinned', { ascending: false })
        .order('published_at', { ascending: false });
      if (error) throw error;
      return data as TenantAnnouncement[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateAnnouncement(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AnnouncementCreateInput) => {
      const { data, error } = await (supabase as any)
        .from('tenant_announcements')
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as TenantAnnouncement;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: announcementKeys.list(tenantId) });
    },
  });
}

export function useUpdateAnnouncement(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: AnnouncementUpdateInput }) => {
      const { data, error } = await (supabase as any)
        .from('tenant_announcements')
        .update(patch)
        .eq('tenant_id', tenantId)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as TenantAnnouncement;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: announcementKeys.list(tenantId) });
    },
  });
}

export function useDeleteAnnouncement(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('tenant_announcements')
        .delete()
        .eq('tenant_id', tenantId)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: announcementKeys.list(tenantId) });
    },
  });
}
